import { getAppData } from '@js/utilities/globalContent.js'

// lodash components
import filter from 'lodash/filter'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import includes from 'lodash/includes'
import remove from 'lodash/remove'
import flatMap from 'lodash/flatMap'
import find from 'lodash/find'
import isDate from 'lodash/isDate'
import orderBy from 'lodash/orderBy'

const pageTypes = {
    current: 'current',
    expired: 'expired',
    declined: 'declined',
}

const territory = process.env.TERRITORY

const helperMethods = {
    // stable sort of personal loans according to business logic
    sortPersonalLoans(offers) {
        let sortedOffers = offers

        // by apr
        sortedOffers = sortBy(sortedOffers, offer => {
            return -offer.apr_display.min
        })

        // sort by confirmed apr for when 2 likelihoods are the same
        sortedOffers = sortBy(sortedOffers, offer => {
            return offer.apr_prefix === 'Confirmed'
        }).reverse()

        // by likelihood
        sortedOffers = sortBy(sortedOffers, offer => {
            return -offer.likelihood
        })

        return sortedOffers
    },
    readDeepProps(obj, prop) {
        const props = prop.split('.')
        let current = obj

        forEach(props, singleProp => {
            if (!current.hasOwnProperty(singleProp)) {
                current = null
                return false
            }

            current = current[singleProp]
            return true
        })

        return current
    },
    sortPreQualifiedOffers(offers) {
        const preQualifiedOffers = remove(
            offers,
            offer =>
                offer.pre_qualified === true && offer.pre_approved === false && !includes(offer.type, 'debt_solution'),
        )

        const preQualifiedNonEqualOffers = preQualifiedOffers
            .filter(offer => offer.loan_amount.min !== state.allAppData.application_data.loan_amount)
            .sort((a, b) => b.loan_amount.min - a.loan_amount.min || a.apr.min - b.apr.min)

        const preQualifiedEqualOffers = preQualifiedOffers
            .filter(offer => offer.loan_amount.min === state.allAppData.application_data.loan_amount)
            .sort((a, b) => b.loan_amount.min - a.loan_amount.min || a.apr.min - b.apr.min)

        return [...preQualifiedEqualOffers, ...preQualifiedNonEqualOffers]
    },
    sortPreApprovedOffers(offers) {
        const preApprovedOffers = remove(
            offers,
            offer => offer.pre_approved === true && !includes(offer.type, 'debt_solution'),
        )

        const preApprovedNonEqualOffers = preApprovedOffers
            .filter(offer => offer.loan_amount.min !== state.allAppData.application_data.loan_amount)
            .sort((a, b) => b.loan_amount.min - a.loan_amount.min || a.apr.min - b.apr.min)

        const preApprovedEqualOffers = preApprovedOffers
            .filter(offer => offer.loan_amount.min === state.allAppData.application_data.loan_amount)
            .sort((a, b) => b.loan_amount.min - a.loan_amount.min || a.apr.min - b.apr.min)

        return [...preApprovedEqualOffers, ...preApprovedNonEqualOffers]
    },
    sortDebtSolutionOffers(offers) {
        const creditOffers = remove(offers, offer => includes(offer.type, 'credit'))

        const debtSolutionOffers = remove(offers, offer => includes(offer.type, 'debt_solution'))

        const otherOffersApr = remove(offers, offer => offer.apr?.min)

        return [...creditOffers, ...otherOffersApr, ...debtSolutionOffers, ...offers]
    },
}

const state = {
    // the API data fetched from polling
    offerResultData: null,
    allAppData: null,
    filterStates: [],
    // different swappable sorts lists
    sortOptionLists: {
        // field = false to ignore (eligibility default ordering)
        default: [
            { name: 'results.sorting.eligibility', field: false, active: true },
            { name: 'results.sorting.monthlyRepayment', field: 'monthly_repayment', active: false },
            { name: 'results.filtering.term', field: 'term', active: false },
            { name: 'results.sorting.apr', field: 'apr_display.min', active: false },
        ],
        abTesting: [
            { name: 'results.sorting.eligibility', label: 'results.label.eligibility', field: false, active: true },
            { name: 'results.sorting.apr', label: 'results.label.apr', field: 'apr_display.min', active: false },
            {
                name: 'results.sorting.monthlyRepayment',
                label: 'results.label.monthlyRepayment',
                field: 'monthly_repayment',
                active: false,
            },
        ],
        creditCardOnly: [
            { name: 'results.sorting.eligibility', field: false, active: true },
            { name: 'results.sorting.btPeriod', field: 'loan_offer.credit_card.bt_term', active: false },
            { name: 'results.sorting.purchasePeriod', field: 'loan_offer.credit_card.purchase_term', active: false },
            { name: 'results.repApr', field: 'rep_apr.min', active: false },
            { name: 'results.creditLimit', field: 'loan_offer.credit_card.credit_limit', active: false },
        ],
    },
    activeSortOptionList: 'default',
    status: pageTypes.current,
    // specific to the button on the desktop filters
    withoutFeesChecked: false,
}

if (territory === 'usa') {
    state.sortOptionLists.default = [
        { name: 'results.sorting.defaultSort', field: false, active: true },
        { name: 'results.sorting.loanAmount', field: 'loan_amount.min', active: false },
        { name: 'results.sorting.apr', field: 'apr_display.min', active: false },
        { name: 'results.sorting.monthlyRepayment', field: 'monthly_repayment', active: false },
        { name: 'results.sorting.totalPayments', field: 'total_repayment', active: false },
    ]
} else if (territory === 'aus') {
    state.sortOptionLists.default = [
        { name: 'results.sorting.defaultSort', field: false, active: true },
        { name: 'results.interestRate', field: 'apr_display.min', active: false },
        { name: 'results.filtering.term', field: 'term', active: false },
        { name: 'results.sorting.monthlyRepayment', field: 'monthly_repayment', active: false },
    ]
}

const mutations = {
    addFilterState(state, payload) {
        if (!state) {
            return
        }
        state.filterStates.push(payload)
    },
    removeFilterState(state, payload) {
        const objectIndex = state?.filterStates?.indexOf(payload)

        if (objectIndex !== -1 || objectIndex !== null) {
            state.filterStates.splice(objectIndex, 1)
        }
    },
    saveAllFilters(state) {
        forEach(state.filterStates, (filterState, index) => {
            filterState.save()
        })
    },
    clearAllFilters(state) {
        forEach(state.filterStates, (filterState, index) => {
            filterState.clear()
        })
    },
    setOfferResultData(state, payload) {
        state.offerResultData = payload
    },
    setAllAppData(state, payload) {
        state.allAppData = payload
    },
    resetPageStatus(state) {
        state.status = pageTypes.current
    },
    setPageStatus(state, payload) {
        state.status = payload
    },
    translateSortOptions(state) {
        forEach(state.sortOptionLists, (list, i) => {
            forEach(list, (option, i) => {
                option.name = state.$t(option.name)
            })
        })
    },
    setActiveSortOptionList(state, payload) {
        if (!isEmpty(state)) {
            state.activeSortOptionList = payload
        } else {
            state.activeSortOptionList = 'default'
        }
    },
}

const getters = {
    declined(state) {
        return state.status === pageTypes.declined
    },
    expired(state) {
        return state.status === pageTypes.expired
    },
    sortOptions(state) {
        return state.sortOptionLists[state.activeSortOptionList]
    },
    getCurrentSortSelected(state, getters) {
        return find(getters.sortOptions, function (sortOption) {
            return sortOption.active
        })
    },
    filtersModified(state) {
        return filter(state.filterStates, function (filterState) {
            return filterState.isModified
        })
    },
    filtersActive(state) {
        return filter(state.filterStates, function (filterState) {
            return filterState.isActive
        })
    },
    offerResultDataFilters(state) {
        return state.offerResultData && state.offerResultData.filters ? state.offerResultData.filters : null
    },
    numberOfOffers(state, getters) {
        return getters.offers.length
    },
    numberOfNonFilteredOffers(state, getters) {
        return getters.offersSorted().length
    },
    numberOfStandardOffers(state, getters) {
        const nonAlternative = filter(getters.offersSorted(), function (offer) {
            return offer.type !== 'alternative'
        })

        return nonAlternative.length
    },
    numberOfExpiredOffers(state, getters) {
        const expiredOffers = filter(getters.offersSorted(), function (offer) {
            if (offer.offer_expires && isDate(new Date(offer.offer_expires))) {
                let date = new Date(offer.offer_expires)
                let now = new Date()

                return now >= date
            }
        })

        return expiredOffers.length
    },
    declinedLenders(state) {
        if (isEmpty(state.offerResultData)) {
            return []
        }

        return state.offerResultData.declined_lenders
    },
    loanValues(state) {
        return state.allAppData && state.allAppData.application_data ? state.allAppData.application_data : null
    },
    offersWithOrderingServiceLogic(state, getters) {
        if (isEmpty(state.offerResultData)) {
            return []
        }

        let offers = flatMap(state.offerResultData.offer_groups, group => {
            return group.offers
        })

        return offers
    },
    // default "Eligibility" sorting.
    // this ordering should be moved to the server when all sites are moved over to this system
    defaultOrderOffers(state, getters) {
        if (isEmpty(state.offerResultData)) {
            return []
        }

        let offers = flatMap(state.offerResultData.offer_groups, group => {
            return group.offers
        })

        const groupPersonalLoansPreapproved = []
        const groupPersonalLoans = []

        const groupPersonalLoansPreapprovedCounterOffers = []
        const groupPersonalLoansCounterOffers = []

        const groupSecuredLoans = []
        const groupCarFinanceOffers = []
        const groupAlternativeCreditCardOffers = []
        const groupAlternativeDebtSolutionOffers = []
        const groupAlternativeOffers = []
        // All left over offers
        const groupTheRest = []

        offers = sortBy(offers, offer => {
            return offer.apr_display.min
        })

        offers = sortBy(offers, offer => {
            return -offer.likelihood
        })

        forEach(offers, (offer, index) => {
            if (offer.type === 'personal_loan') {
                if (
                    offer.apr_prefix === 'Confirmed' &&
                    (offer.group === 'prequal_same_loan_amount' ||
                        offer.group === 'prequal_different_loan_amount' ||
                        offer.likelihood === 100)
                ) {
                    if (offer.group === 'prequal_same_loan_amount') {
                        groupPersonalLoansPreapproved.push(offer)
                    } else {
                        groupPersonalLoansPreapprovedCounterOffers.push(offer)
                    }
                } else if (
                    !(
                        // isSameAmountAndTerm
                        (
                            parseFloat(getters.loanValues.loan_term) === parseFloat(offer.term_display) &&
                            parseFloat(getters.loanValues.loan_amount) === offer.loan_amount.min &&
                            !offer.loan_amount.max
                        )
                    )
                ) {
                    groupPersonalLoansCounterOffers.push(offer)
                } else {
                    groupPersonalLoans.push(offer)
                }
            } else if (offer.type === 'secured_loan') {
                groupSecuredLoans.push(offer)
            } else if (offer.type === 'car_finance') {
                groupCarFinanceOffers.push(offer)
            } else if (includes(offer.type, 'credit')) {
                groupAlternativeCreditCardOffers.push(offer)
            } else if (includes(offer.type, 'debt_solution')) {
                groupAlternativeDebtSolutionOffers.push(offer)
            } else if (offer.type === 'alternative') {
                groupAlternativeOffers.push(offer)
            } else {
                groupTheRest.push(offer)
            }
        })

        if (territory === 'usa') {
            const groupPersonalLoansSorted = helperMethods.sortPersonalLoans(groupPersonalLoans)
            const groupPersonalLoansCounterOffersSorted = helperMethods.sortPersonalLoans(
                groupPersonalLoansCounterOffers,
            )

            let usaOrderOffers = [
                ...groupPersonalLoansPreapproved,
                ...groupPersonalLoansSorted,
                ...groupSecuredLoans,
                ...groupPersonalLoansPreapprovedCounterOffers,
                ...groupPersonalLoansCounterOffersSorted,
                ...groupCarFinanceOffers,
                ...groupTheRest,
                ...groupAlternativeCreditCardOffers,
                ...groupAlternativeDebtSolutionOffers,
                ...groupAlternativeOffers,
            ]

            let preQualifiedOffers = helperMethods.sortPreQualifiedOffers(usaOrderOffers)
            let preApprovedOffers = helperMethods.sortPreApprovedOffers(usaOrderOffers)

            usaOrderOffers = helperMethods.sortDebtSolutionOffers(usaOrderOffers)

            return [...preApprovedOffers, ...preQualifiedOffers, ...usaOrderOffers]
        }

        // other territories
        return [
            ...helperMethods.sortPersonalLoans([
                ...groupPersonalLoansPreapproved,
                ...groupPersonalLoans,
                ...groupSecuredLoans,
                ...groupPersonalLoansPreapprovedCounterOffers,
                ...groupPersonalLoansCounterOffers,
                ...groupCarFinanceOffers,
                ...groupTheRest,
            ]),
            ...groupAlternativeCreditCardOffers,
            ...groupAlternativeDebtSolutionOffers,
            ...groupAlternativeOffers,
        ]
    },
    // offers sorted by the sort panel
    offersSorted:
        (state, getters) =>
        (sort = null) => {
            let currentSort = getters.getCurrentSortSelected?.field

            if (sort) {
                currentSort = sort
            }

            if (!currentSort) {
                return getters.defaultOrderOffers
            }

            let sortedOffers = getters.defaultOrderOffers

            let nonAlternative = filter(sortedOffers, function (offer) {
                return offer.type !== 'alternative'
            })

            const alternative = filter(sortedOffers, function (offer) {
                return offer.type === 'alternative'
            })

            nonAlternative = sortBy(nonAlternative, offer => {
                return helperMethods.readDeepProps(offer, currentSort)
            })

            sortedOffers = [...nonAlternative, ...alternative]

            return sortedOffers
        },
    getFirstOrderedOfferByField:
        (state, getters) =>
        (key = null) => {
            const sortedOffers = getters.offersSorted(key)

            return sortedOffers.length > 0 ? sortedOffers[0] : null
        },
    offersWithLendingTreeSorting(state, getters) {
        let offers = flatMap(state?.offerResultData?.offer_groups, group => {
            return group.offers
        })

        offers = orderBy(offers, [offer => offer.likelihood, offer => offer.apr_display.min], ['desc', 'asc'])

        const nonAlternative = filter(offers, function (offer) {
            return offer.type !== 'alternative'
        })

        let alternative = filter(offers, function (offer) {
            return offer.type === 'alternative'
        })

        alternative = sortBy(alternative, ['sequence'])

        return [...nonAlternative, ...alternative]
    },
    // offers with all rules taken into account
    offers(state, getters) {
        if (process.env.IS_VERVENT && !getters.filtersActive.length) {
            return getters.offersWithOrderingServiceLogic
        }

        if (process.env.IS_LENDINDTREE) {
            return getters.offersWithLendingTreeSorting
        }

        if (!getters.filtersActive.length) {
            return getters.offersSorted()
        }

        return filter(getters.offersSorted(), offer => {
            let showOffer = true
            forEach(getters.filtersActive, (filterState, index) => {
                if (!filterState.filterItem(offer)) {
                    showOffer = false
                    return false
                }
            })

            return showOffer
        })
    },
}

const actions = {
    async refreshAppData(context, payload) {
        const appData = await getAppData(payload.store, payload.ref)

        if (appData) {
            context.commit('setAllAppData', appData)
            return true
        }

        return false
    },
}

export { state, mutations, getters, actions }
